import { useState, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Paperclip, X, Download, Loader2, FileImage, FileText } from "lucide-react";

const MAX_FILES = 3;

const FileAttachments = ({ assetId, collection, recordId, archivos = [], onUpdate }) => {
  const { token } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (archivos.length >= MAX_FILES) {
      toast.error(`Máximo ${MAX_FILES} archivos por registro`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(
        `${API}/vehicles/${assetId}/upload/${collection}/${recordId}`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      onUpdate([...archivos, res.data]);
      toast.success("Archivo subido");
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al subir archivo";
      console.error("Upload error:", err);
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await axios.delete(
        `${API}/vehicles/${assetId}/files/${collection}/${recordId}/${fileId}`,
        { withCredentials: true }
      );
      onUpdate(archivos.filter((a) => a.id !== fileId));
      toast.success("Archivo eliminado");
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Error al eliminar");
    }
  };

  const handleDownload = async (archivo) => {
    try {
      const res = await axios.get(`${API}/vehicles/files/${archivo.storage_path}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = archivo.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Error al descargar");
    }
  };

  const isImage = (ct) => ct?.startsWith("image/");
  const getIcon = (ct) => isImage(ct) ? FileImage : FileText;
  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-2">
      {/* File list */}
      {archivos.length > 0 && (
        <div className="space-y-1.5">
          {archivos.map((archivo) => {
            const Icon = getIcon(archivo.content_type);
            return (
              <div key={archivo.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm group">
                <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="flex-1 truncate text-slate-700">{archivo.filename}</span>
                <span className="text-xs text-slate-400">{formatSize(archivo.size)}</span>
                <Button
                  variant="ghost" size="icon" className="h-6 w-6 opacity-60 hover:opacity-100"
                  onClick={() => handleDownload(archivo)}
                  data-testid={`download-file-${archivo.id}`}
                >
                  <Download className="w-3.5 h-3.5 text-blue-600" />
                </Button>
                <Button
                  variant="ghost" size="icon" className="h-6 w-6 opacity-60 hover:opacity-100"
                  onClick={() => handleDelete(archivo.id)}
                  data-testid={`delete-file-${archivo.id}`}
                >
                  <X className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload button */}
      {archivos.length < MAX_FILES && (
        <div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={handleUpload}
          />
          <Button
            variant="outline" size="sm"
            className="text-xs"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`upload-file-${recordId}`}
          >
            {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Paperclip className="w-3 h-3 mr-1" />}
            {uploading ? "Subiendo..." : `Adjuntar (${archivos.length}/${MAX_FILES})`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileAttachments;
